import { useState, useEffect, useCallback } from 'react';
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead, type Notification } from '@/lib/notifications-api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Load initial notifications
  const loadNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const [notifs, count] = await Promise.all([
        getNotifications({ limit: 50 }),
        getUnreadCount(),
      ]);
      setNotifications(notifs);
      setUnreadCount(count);
      setError(null);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Mark as read
  const handleMarkAsRead = useCallback(async (notificationId: string) => {
    try {
      await markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  }, []);

  // Mark all as read
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await markAllAsRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  }, []);

  // SSE connection
  useEffect(() => {
    let eventSource: EventSource | null = null;

    const connectSSE = () => {
      try {
        eventSource = new EventSource(`${API_URL}/api/notifications/stream`, {
          withCredentials: true,
        });

        eventSource.onopen = () => {
          console.log('[SSE] Connected to notifications stream');
          setIsConnected(true);
        };

        eventSource.addEventListener('connected', (event) => {
          const data = JSON.parse(event.data);
          console.log('[SSE] Connection confirmed:', data);
        });

        eventSource.addEventListener('notification', (event) => {
          const notification: Notification = JSON.parse(event.data);
          console.log('[SSE] Received notification:', notification);

          // Add to notifications list
          setNotifications((prev) => [notification, ...prev]);

          // Increment unread count
          if (!notification.isRead) {
            setUnreadCount((prev) => prev + 1);
          }

          // Optional: Show browser notification
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(notification.title, {
              body: notification.message,
              icon: notification.icon || '/logo.png',
              tag: notification.id,
            });
          }
        });

        eventSource.onerror = (err) => {
          console.error('[SSE] Connection error:', err);
          setIsConnected(false);
          eventSource?.close();

          // Reconnect after 5 seconds
          setTimeout(() => {
            console.log('[SSE] Attempting to reconnect...');
            connectSSE();
          }, 5000);
        };
      } catch (err) {
        console.error('[SSE] Failed to connect:', err);
        setIsConnected(false);
      }
    };

    // Initial load
    loadNotifications();

    // Connect to SSE
    connectSSE();

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Cleanup
    return () => {
      if (eventSource) {
        eventSource.close();
        setIsConnected(false);
      }
    };
  }, [loadNotifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    isConnected,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    refresh: loadNotifications,
  };
}
